import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

export interface MultiDetailOption<T extends object> {
  id: number | string;
  value: T;
}

interface DetailFieldConfig<T extends object> {
  field: keyof T;
  variant?: 'body1' | 'body2' | 'caption';
  bold?: boolean;
  prefix?: string;
}

interface CustomAutoCompleteMultiDetailedProps<T extends object> {
  label?: string;
  options: MultiDetailOption<T>[];
  value?: MultiDetailOption<T> | null;
  onChange: (value: MultiDetailOption<T> | null) => void;
  mainLabel: keyof T;
  detailFields?: DetailFieldConfig<T>[];

  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: 'small' | 'medium';
  noOptionsText?: string;
}

function CustomAutoCompleteMultiDetailed<T extends object>({
  label,
  options,
  value,
  onChange,
  mainLabel,
  detailFields = [],
  placeholder,
  disabled = false,
  className,
  noOptionsText = 'No options',
  size = 'small',
}: Readonly<CustomAutoCompleteMultiDetailedProps<T>>) {
  const [inputValue, setInputValue] = useState('');

  /* ---------- SAFE MANUAL FILTER ---------- */
  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return options;

    const search = inputValue.toLowerCase();

    return options.filter((option) => {
      const mainText = String(option.value?.[mainLabel] ?? '').toLowerCase();
      if (mainText.includes(search)) return true;

      return detailFields.some((df) =>
        String(option.value?.[df.field] ?? '')
          .toLowerCase()
          .includes(search)
      );
    });
  }, [inputValue, options, mainLabel, detailFields]);

  return (
    <Autocomplete
      options={filteredOptions}
      value={value}
      inputValue={inputValue}
      disabled={disabled}
      className={className}
      size={size}
      noOptionsText={noOptionsText}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      getOptionLabel={(option) => String(option.value?.[mainLabel] ?? '')}
      onChange={(_, newValue) => onChange(newValue)}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box display="flex" flexDirection="column">
            {/* MAIN LABEL */}
            <Typography variant="body1" fontWeight={600}>
              {String(option.value[mainLabel])}
            </Typography>

            {/* DETAIL FIELDS */}
            {detailFields.map((df, index) => (
              <Typography
                key={index}
                variant={df.variant ?? 'caption'}
                fontWeight={df.bold ? 600 : 400}
                color="text.secondary"
              >
                {df.prefix ?? ''}
                {String(option.value[df.field] ?? 'N/A')}
              </Typography>
            ))}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={placeholder} />
      )}
      slotProps={{
        clearIndicator: {
          sx: {
            '& svg': {
              fontSize: 16,
            },
          },
        },
      }}
    />
  );
}

export default CustomAutoCompleteMultiDetailed;
